import { useState, useEffect, useRef } from 'react';
import {
  X, Zap, RefreshCw, Sparkles, Send, Copy, ExternalLink, Award,
  Phone, Mail, MessageSquare, Linkedin, Code2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import api, { API_BASE_URL } from '../api';

const LOADING_STEPS = [
  "Structuring sales context...",
  "Drafting personalized cold email sequences...",
  "Formatting LinkedIn connection requests & pitches...",
  "Drafting WhatsApp conversational hooks...",
  "Compiling discovery scripts and handling client objections...",
  "Inlining premium B2B proposal HTML layout...",
  "Structuring CRM probability scores & contact timing..."
];

export default function SalesAgentModal({
  isOpen,
  onClose,
  leadId,
  businessName,
  salesAgent,
  onRefresh,
  generationStatus
}) {
  const [activeTab, setActiveTab] = useState('crm');
  const [isInitializing, setIsInitializing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(null);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  const [activeEmailSubtab, setActiveEmailSubtab] = useState('email_1');

  const [chatMessage, setChatMessage] = useState('');
  const [isChatSending, setIsChatSending] = useState(false);
  const chatBottomRef = useRef(null);

  const [copiedField, setCopiedField] = useState(null);

  const [proposalVersions, setProposalVersions] = useState([]);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isEmailingPDF, setIsEmailingPDF] = useState(false);

  const [campaignEmails, setCampaignEmails] = useState([]);

  const fetchCampaign = async () => {
    if (!leadId) return;
    try {
      const response = await api.get(`/outreach/campaign/${leadId}`);
      setCampaignEmails(response.data);
    } catch (err) {
      console.error("Failed to fetch campaign", err);
    }
  };

  const fetchProposalVersions = async () => {
    if (!leadId) return;
    try {
      const response = await api.get(`/leads/${leadId}/proposals/versions`);
      setProposalVersions(response.data);
    } catch (err) {
      console.error("Failed to fetch proposal versions", err);
    }
  };

  useEffect(() => {
    if (isOpen && leadId) {
      fetchCampaign();
      fetchProposalVersions();
    }
  }, [isOpen, leadId, activeTab]);

  const handleTriggerCampaign = async () => {
    if (!leadId) return;
    try {
      await api.post(`/outreach/trigger-sequence/${leadId}`);
      fetchCampaign();
      onRefresh();
    } catch (err) {
      console.error(err);
      alert("Failed to start campaign");
    }
  };

  const handleSimulateOpen = async (emailId) => {
    try {
      await api.get(`/outreach/track/open/${emailId}`);
      fetchCampaign();
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSimulateClick = async (emailId) => {
    try {
      await api.get(`/outreach/track/click/${emailId}?url=https://example.com`);
      fetchCampaign();
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSimulateReply = async (emailId) => {
    try {
      await api.post(`/outreach/webhook/reply/${emailId}`);
      fetchCampaign();
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSimulateBounce = async (emailId) => {
    try {
      await api.post(`/outreach/webhook/bounce/${emailId}`);
      fetchCampaign();
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  const isGenerating = generationStatus?.crawling === 'running' ||
    generationStatus?.competitor_analysis === 'running' ||
    generationStatus?.proposal === 'running' ||
    generationStatus?.emails === 'running';

  useEffect(() => {
    let interval;
    if (isInitializing || isGenerating) {
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev + 1) % LOADING_STEPS.length);
      }, 2000);
    } else {
      setLoadingStepIndex(0);
    }
    return () => clearInterval(interval);
  }, [isInitializing, isGenerating]);

  useEffect(() => {
    let interval;
    if (isGenerating && !isInitializing) {
      interval = setInterval(() => { onRefresh(); }, 3000);
    }
    return () => clearInterval(interval);
  }, [isGenerating, isInitializing]);

  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [salesAgent?.conversation_history]);

  if (!isOpen) return null;

  const handleInitialize = async () => {
    if (!leadId) return;
    setIsInitializing(true);
    try {
      await api.post(`/leads/${leadId}/sales-agent/init`);
      await onRefresh();
    } catch (err) {
      console.error(err);
      alert("Failed to initialize AI Sales Agent. Make sure you scanned the website and competitors first.");
    } finally {
      setIsInitializing(false);
    }
  };

  const handleRegenerate = async (target) => {
    if (!leadId) return;
    setIsRegenerating(target);
    try {
      if (target === 'proposal_pdf') {
        setIsGeneratingPDF(true);
        await api.post(`/leads/${leadId}/proposals/generate`);
        await fetchProposalVersions();
      } else {
        await api.post(`/leads/${leadId}/sales-agent/regenerate`, { target });
      }
      await onRefresh();
    } catch (err) {
      console.error(err);
      alert(`Failed to regenerate ${target}`);
    } finally {
      setIsRegenerating(null);
      setIsGeneratingPDF(false);
    }
  };

  const handleDownloadPDF = (version) => {
    const entry = proposalVersions.find((v) => v.version === version);
    if (!entry) return;
    window.open(`${API_BASE_URL}${entry.pdf_path}`, '_blank');
  };

  const handleEmailPDF = async (version) => {
    if (!leadId) return;
    setIsEmailingPDF(true);
    try {
      await api.post(`/leads/${leadId}/proposals/email/${version}`);
      alert('Proposal email queued successfully.');
    } catch (err) {
      console.error(err);
      alert('Failed to email proposal.');
    } finally {
      setIsEmailingPDF(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim() || !leadId || isChatSending) return;
    setIsChatSending(true);
    const msg = chatMessage;
    setChatMessage('');
    try {
      await api.post(`/leads/${leadId}/sales-agent/chat`, { message: msg });
      onRefresh();
    } catch (err) {
      console.error(err);
      alert("Chat failed to send.");
    } finally {
      setIsChatSending(false);
    }
  };

  const copyToClipboard = (text, fieldId) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getPriorityColor = (p) => {
    if (p === 'High') return 'text-red-400 bg-red-500/10 border-red-500/20';
    if (p === 'Medium') return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
    return 'text-green-400 bg-green-500/10 border-green-500/20';
  };

  const getTempColor = (t) => {
    if (t === 'Hot') return 'text-red-400 bg-red-500/10 border-red-500/20';
    if (t === 'Warm') return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
    return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 overflow-y-auto">
      {(isInitializing || isGenerating) && (
        <div className="absolute inset-0 z-50 bg-slate-900/90 backdrop-blur-sm flex flex-col items-center justify-center rounded-3xl">
          <div className="relative w-24 h-24 mb-8">
            <div className="absolute inset-0 border-4 border-indigo-500/20 rounded-full animate-pulse"></div>
            <div className="absolute inset-0 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            <div className="absolute inset-4 bg-indigo-500/10 rounded-full flex items-center justify-center">
              <Sparkles className="text-indigo-400 animate-pulse" size={28} />
            </div>
          </div>
          <h3 className="text-2xl font-bold tracking-tight text-white mb-2">Deploying AI Sales Agent</h3>
          <p className="text-indigo-400/80 text-sm animate-pulse max-w-md h-8 transition-all">{LOADING_STEPS[loadingStepIndex]}</p>
          <div className="w-64 h-1 bg-white/[0.06] rounded-full overflow-hidden mt-6">
            <div className="h-full bg-indigo-500 animate-[shimmer_2s_infinite]" style={{
              backgroundImage: 'linear-gradient(90deg, transparent, #818cf8, transparent)', backgroundSize: '200% 100%'
            }}></div>
          </div>
        </div>
      )}

      <div className="glass-card w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-3xl border border-white/[0.08] shadow-2xl flex flex-col bg-slate-900/90 text-slate-100">

        <div className="p-6 border-b border-white/[0.06] flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-900/40 backdrop-blur-md">
          <div className="flex items-center gap-2">
            <span className="p-2 bg-indigo-500/10 text-indigo-400 rounded-xl border border-indigo-500/20 animate-pulse"><Sparkles size={20} /></span>
            <div>
              <h2 className="text-xl font-bold tracking-tight text-white">AI Sales Agent Center</h2>
              <p className="text-xs text-slate-400 font-mono">Lead Outreach Strategy: {businessName}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-stretch md:self-auto justify-end">
            {!salesAgent?.email_sequence && leadId && (
              <button onClick={handleInitialize} disabled={isInitializing}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold rounded-xl transition-all border border-indigo-400/20 shadow-lg shadow-indigo-600/15">
                <Sparkles size={14} /> Initialize AI Agent
              </button>
            )}
            <button onClick={onClose} className="p-2 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.06] rounded-xl transition-colors text-slate-400 hover:text-white">
              <X size={18} />
            </button>
          </div>
        </div>

        {!salesAgent?.email_sequence ? (
          <div className="flex-1 flex flex-col items-center justify-center py-28 text-slate-400">
            <Sparkles size={52} className="text-slate-700 mb-4 animate-pulse" />
            <p className="text-sm font-medium">Activate the AI Sales Agent for this lead to generate sequences, call sheets, HTML proposals, and advisors.</p>
            <button onClick={handleInitialize} className="mt-5 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold flex items-center gap-2 transition-all border border-indigo-500/20 shadow-lg shadow-indigo-600/15">
              <Sparkles size={14} /> Deploy AI Agent Outreach
            </button>
          </div>
        ) : (
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden">

            <div className="w-full md:w-56 border-r border-white/[0.06] flex flex-row md:flex-col overflow-x-auto md:overflow-y-auto p-4 gap-1.5 shrink-0 bg-slate-950/20">
              {[
                { id: 'crm', label: 'CRM Recommendations', icon: Award },
                { id: 'campaign', label: 'Campaign Sequence', icon: Zap },
                { id: 'email', label: 'Email Sequence', icon: Mail },
                { id: 'linkedin', label: 'LinkedIn Social', icon: Linkedin },
                { id: 'whatsapp', label: 'WhatsApp Mobile', icon: MessageSquare },
                { id: 'call', label: 'Cold Call Script', icon: Phone },
                { id: 'proposal', label: 'Growth Proposal', icon: Code2 },
                { id: 'chat', label: 'AI Lead Advisor', icon: Sparkles }
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <button key={item.id} onClick={() => setActiveTab(item.id)}
                    className={cn("w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all shrink-0 md:shrink border",
                      activeTab === item.id ? "bg-indigo-600 text-white border-indigo-500 shadow-md shadow-indigo-600/10 font-bold" : "bg-transparent text-slate-400 border-transparent hover:bg-white/[0.05] hover:text-slate-200")}>
                    <Icon size={14} /> {item.label}
                  </button>
                );
              })}
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-slate-900/10 flex flex-col min-h-0">

              {activeTab === 'crm' && salesAgent.crm_suggestions && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-base font-bold text-white flex items-center gap-2"><Award size={18} className="text-indigo-400" /> CRM Outreach Priority Suggestions</h3>
                    <button onClick={() => handleRegenerate('crm')} disabled={isRegenerating === 'crm'} className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5">
                      <RefreshCw size={12} className={cn(isRegenerating === 'crm' && 'animate-spin')} /> Regenerate
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] text-center space-y-1">
                      <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Lead Warmth</span>
                      <span className={cn("px-2.5 py-1 rounded-lg text-xs font-bold w-fit mx-auto mt-2 block border", getTempColor(salesAgent.crm_suggestions.temperature))}>{salesAgent.crm_suggestions.temperature} Lead</span>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] text-center space-y-1">
                      <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Outreach Priority</span>
                      <span className={cn("px-2.5 py-1 rounded-lg text-xs font-bold w-fit mx-auto mt-2 block border", getPriorityColor(salesAgent.crm_suggestions.priority))}>{salesAgent.crm_suggestions.priority} Priority</span>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] text-center space-y-1 flex flex-col justify-between">
                      <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Best Channel</span>
                      <span className="text-sm font-bold text-white mt-1.5 block">{salesAgent.crm_suggestions.best_outreach_channel}</span>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] text-center space-y-1">
                      <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Conversion Probability</span>
                      <div className="text-2xl font-black text-green-400 mt-1">{salesAgent.crm_suggestions.conversion_probability}%</div>
                    </div>
                  </div>
                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-2">
                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Best Contact Time</span>
                    <p className="text-sm text-slate-300 font-medium">{salesAgent.crm_suggestions.best_contact_time}</p>
                  </div>
                </div>
              )}

              {activeTab === 'campaign' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-base font-bold text-white flex items-center gap-2"><Zap size={18} className="text-indigo-400" /> Sequence Outreach Campaign Status</h3>
                    {campaignEmails.length > 0 && (
                      <button onClick={handleTriggerCampaign} className="text-xs px-3 py-1.5 bg-red-600/10 hover:bg-red-600/20 rounded-lg text-red-400 font-bold transition-all border border-red-500/20">Reset Campaign</button>
                    )}
                  </div>

                  {campaignEmails.length === 0 ? (
                    <div className="bg-slate-900/50 p-8 rounded-3xl border border-white/[0.06] text-center space-y-4 py-16">
                      <Zap size={44} className="text-slate-600 mx-auto animate-pulse" />
                      <div className="space-y-1">
                        <h4 className="text-sm font-bold text-white">No active campaign for this lead</h4>
                        <p className="text-xs text-slate-400">Launch an automated sequence campaign starting with AI Email Step 1.</p>
                      </div>
                      <button onClick={handleTriggerCampaign} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold flex items-center gap-2 mx-auto border border-indigo-500/20 shadow-lg shadow-indigo-600/10">
                        <Zap size={12} /> Launch Automated Campaign
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {campaignEmails.map((email) => (
                        <div key={email.id} className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-4">
                          <div className="flex justify-between items-start md:items-center flex-col md:flex-row gap-2 border-b border-white/[0.06] pb-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-bold px-2 py-0.5 rounded-lg uppercase">Step {email.step_number}</span>
                                <h4 className="text-sm font-bold text-white">{email.subject}</h4>
                              </div>
                              <p className="text-[10px] text-slate-400 font-mono mt-1">Recipient: {email.recipient}</p>
                            </div>
                            <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold uppercase",
                              email.status === 'sent' ? "bg-green-500/10 text-green-400 border border-green-500/20" :
                                email.status === 'failed' ? "bg-red-500/10 text-red-400 border border-red-500/20" :
                                  "bg-orange-500/10 text-orange-400 border border-orange-500/20")}>{email.status}</span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                            <div className="bg-white/[0.02] p-2 rounded-xl border border-white/[0.06]">
                              <span className="text-[9px] text-slate-500 uppercase tracking-wider block">Opens</span>
                              <span className="text-base font-black text-white">{email.opens_count}</span>
                            </div>
                            <div className="bg-white/[0.02] p-2 rounded-xl border border-white/[0.06]">
                              <span className="text-[9px] text-slate-500 uppercase tracking-wider block">Clicks</span>
                              <span className="text-base font-black text-white">{email.clicks_count}</span>
                            </div>
                            <div className="bg-white/[0.02] p-2 rounded-xl border border-white/[0.06]">
                              <span className="text-[9px] text-slate-500 uppercase tracking-wider block">Replied</span>
                              <span className={cn("text-xs font-bold block mt-1", email.replied ? "text-green-400" : "text-slate-500")}>{email.replied ? "Yes" : "No"}</span>
                            </div>
                            <div className="bg-white/[0.02] p-2 rounded-xl border border-white/[0.06]">
                              <span className="text-[9px] text-slate-500 uppercase tracking-wider block">Bounced</span>
                              <span className={cn("text-xs font-bold block mt-1", email.bounced ? "text-red-400" : "text-slate-500")}>{email.bounced ? "Yes" : "No"}</span>
                            </div>
                          </div>

                          {email.status === 'sent' && (
                            <div className="pt-2 border-t border-white/[0.06] flex flex-wrap gap-2">
                              <button onClick={() => handleSimulateOpen(email.id)} className="px-2.5 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 text-[10px] font-bold rounded-lg border border-white/[0.06] flex items-center gap-1 transition-colors">👁️ Simulate Open</button>
                              <button onClick={() => handleSimulateClick(email.id)} className="px-2.5 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 text-[10px] font-bold rounded-lg border border-white/[0.06] flex items-center gap-1 transition-colors">🔗 Simulate Click</button>
                              {!email.replied && !email.bounced && (
                                <>
                                  <button onClick={() => handleSimulateReply(email.id)} className="px-2.5 py-1.5 bg-green-500/10 hover:bg-green-500/20 text-green-400 text-[10px] font-bold rounded-lg border border-green-500/10 flex items-center gap-1 transition-colors">💬 Simulate Reply</button>
                                  <button onClick={() => handleSimulateBounce(email.id)} className="px-2.5 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[10px] font-bold rounded-lg border border-red-500/10 flex items-center gap-1 transition-colors">⚠️ Simulate Bounce</button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'email' && salesAgent.email_sequence && (
                <div className="space-y-6 flex-1 flex flex-col min-h-0">
                  <div className="flex justify-between items-center">
                    <div className="flex gap-2">
                      {['email_1', 'follow_up_1', 'follow_up_2', 'final_follow_up'].map((subtab) => (
                        <button key={subtab} onClick={() => setActiveEmailSubtab(subtab)}
                          className={cn("px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-xl transition-all border",
                            activeEmailSubtab === subtab ? "bg-white/[0.1] text-white border-white/15" : "bg-transparent text-slate-400 border-transparent hover:text-slate-200")}>
                          {subtab === 'email_1' ? '1. Cold Intro' : subtab === 'follow_up_1' ? '2. Follow-Up' : subtab === 'follow_up_2' ? '3. Gaps Pitch' : '4. Final Nudge'}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => handleRegenerate('email')} disabled={isRegenerating === 'email'} className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5">
                      <RefreshCw size={12} className={cn(isRegenerating === 'email' && 'animate-spin')} /> Regenerate Sequence
                    </button>
                  </div>

                  {(() => {
                    const email = salesAgent.email_sequence[activeEmailSubtab];
                    if (!email) return null;
                    return (
                      <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-4 flex-1 flex flex-col">
                        <div className="flex justify-between items-center border-b border-white/[0.06] pb-3">
                          <div className="space-y-1">
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Subject Line</span>
                            <span className="text-sm font-bold text-white">{email.subject}</span>
                          </div>
                          <button onClick={() => copyToClipboard(email.subject, 'subj')} className="p-2 hover:bg-white/[0.05] text-slate-400 hover:text-white rounded-lg transition-colors flex items-center gap-1.5 text-xs font-semibold">
                            <Copy size={12} /> {copiedField === 'subj' ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                        <div className="flex justify-between items-center border-b border-white/[0.06] pb-3">
                          <div className="space-y-1">
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Preview Snippet</span>
                            <span className="text-xs text-slate-400 italic">{email.preview_text}</span>
                          </div>
                          <button onClick={() => copyToClipboard(email.preview_text, 'prev')} className="p-2 hover:bg-white/[0.05] text-slate-400 hover:text-white rounded-lg transition-colors flex items-center gap-1.5 text-xs font-semibold">
                            <Copy size={12} /> {copiedField === 'prev' ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                        <div className="flex-1 flex flex-col min-h-0 bg-slate-950/20 p-4 rounded-xl border border-white/[0.06] relative">
                          <button onClick={() => copyToClipboard(`${email.body}\n\n${email.cta}\n\n${email.signature}`, 'body')} className="absolute top-3 right-3 p-2 hover:bg-white/[0.05] text-slate-400 hover:text-white rounded-lg transition-colors flex items-center gap-1.5 text-xs font-semibold">
                            <Copy size={12} /> {copiedField === 'body' ? 'Copied Full Content' : 'Copy Full'}
                          </button>
                          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block mb-3">Email Body</span>
                          <div className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed overflow-y-auto pr-12 flex-1">
                            {email.body}{"\n\n"}<span className="font-bold text-indigo-400">{email.cta}</span>{"\n\n"}{email.signature}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {activeTab === 'linkedin' && salesAgent.linkedin && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-base font-bold text-white flex items-center gap-2"><Linkedin size={18} className="text-indigo-400" /> LinkedIn Outreach Sequence</h3>
                    <button onClick={() => handleRegenerate('linkedin')} disabled={isRegenerating === 'linkedin'} className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5">
                      <RefreshCw size={12} className={cn(isRegenerating === 'linkedin' && 'animate-spin')} /> Regenerate
                    </button>
                  </div>
                  <div className="space-y-4">
                    {[
                      { key: 'connection_request', title: '1. Connection Invitation (300 character limit)' },
                      { key: 'first_message', title: '2. First Message (Introductory Pitch)' },
                      { key: 'second_message', title: '3. Second Message (Value Gaps comparison)' },
                      { key: 'follow_up', title: '4. Follow-up Nudge' }
                    ].map((msg) => {
                      const text = salesAgent.linkedin[msg.key];
                      return (
                        <div key={msg.key} className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] space-y-3 relative">
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider block">{msg.title}</span>
                            <button onClick={() => copyToClipboard(text, msg.key)} className="p-1 text-slate-400 hover:text-white flex items-center gap-1 text-[10px]">
                              <Copy size={10} /> {copiedField === msg.key ? 'Copied' : 'Copy'}
                            </button>
                          </div>
                          <p className="text-xs text-slate-300 leading-relaxed pr-8">{text}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeTab === 'whatsapp' && salesAgent.whatsapp && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-base font-bold text-white flex items-center gap-2"><MessageSquare size={18} className="text-indigo-400" /> WhatsApp Mobile Hooks</h3>
                    <button onClick={() => handleRegenerate('whatsapp')} disabled={isRegenerating === 'whatsapp'} className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5">
                      <RefreshCw size={12} className={cn(isRegenerating === 'whatsapp' && 'animate-spin')} /> Regenerate
                    </button>
                  </div>
                  <div className="space-y-4">
                    {[
                      { key: 'opening', title: 'Conversational Opening Message' },
                      { key: 'follow_up', title: 'Follow-up Value Widget Offer' },
                      { key: 'reminder', title: 'Short Objections Nudge' }
                    ].map((msg) => {
                      const text = salesAgent.whatsapp[msg.key];
                      return (
                        <div key={msg.key} className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] space-y-3 relative">
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider block">{msg.title}</span>
                            <button onClick={() => copyToClipboard(text, msg.key)} className="p-1 text-slate-400 hover:text-white flex items-center gap-1 text-[10px]">
                              <Copy size={10} /> {copiedField === msg.key ? 'Copied' : 'Copy'}
                            </button>
                          </div>
                          <p className="text-xs text-slate-300 leading-relaxed font-mono pr-8">{text}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeTab === 'call' && salesAgent.call_script && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-base font-bold text-white flex items-center gap-2"><Phone size={18} className="text-indigo-400" /> Cold Call Sales Playbook</h3>
                    <button onClick={() => handleRegenerate('script')} disabled={isRegenerating === 'script'} className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5">
                      <RefreshCw size={12} className={cn(isRegenerating === 'script' && 'animate-spin')} /> Regenerate Script
                    </button>
                  </div>
                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-3">
                    <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider block">1. The Hook / Opening</span>
                    <p className="text-xs text-slate-300 leading-relaxed">{salesAgent.call_script.opening}</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-3">
                      <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider block">2. Discovery Questions</span>
                      <ul className="space-y-1.5">
                        {salesAgent.call_script.discovery_questions.map((q, i) => (
                          <li key={i} className="text-xs text-slate-300 flex items-start gap-2"><span className="text-indigo-400 font-bold shrink-0">?</span><span>{q}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-3">
                      <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider block">3. Pain Points to Emphasize</span>
                      <ul className="space-y-1.5">
                        {salesAgent.call_script.pain_points.map((p, i) => (
                          <li key={i} className="text-xs text-slate-300 flex items-start gap-2"><span className="text-red-400 font-bold shrink-0">•</span><span>{p}</span></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-3">
                    <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider block">4. Competitor Comparisons to Raise</span>
                    <ul className="space-y-1.5">
                      {salesAgent.call_script.competitor_talking_points.map((p, i) => (
                        <li key={i} className="text-xs text-slate-300 flex items-start gap-2"><span className="text-indigo-400 font-bold shrink-0">→</span><span>{p}</span></li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-3">
                    <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider block">5. Objection Handling Playbook</span>
                    <div className="space-y-3">
                      {salesAgent.call_script.objection_handling.map((obj, i) => (
                        <div key={i} className="p-3 bg-slate-950/20 border border-white/[0.06] rounded-xl space-y-1">
                          <span className="text-[10px] font-bold uppercase text-orange-400">Objection: "{obj.objection}"</span>
                          <p className="text-xs text-slate-300 leading-relaxed font-serif">" {obj.response} "</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-3">
                    <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider block">6. Closing & Ask</span>
                    <p className="text-xs text-slate-300 leading-relaxed">{salesAgent.call_script.closing}</p>
                  </div>
                </div>
              )}

              {activeTab === 'proposal' && (
                <div className="space-y-6 flex-1 flex flex-col min-h-0">
                  <div className="flex justify-between items-center">
                    <h3 className="text-base font-bold text-white flex items-center gap-2"><Code2 size={18} className="text-indigo-400" /> Executive PDF Proposal</h3>
                    <div className="flex gap-2">
                      {proposalVersions.length > 0 && (
                        <>
                          <button onClick={() => handleDownloadPDF(proposalVersions[0].version)} className="text-xs px-3 py-1 bg-indigo-600/10 hover:bg-indigo-600/20 rounded-lg text-indigo-400 flex items-center gap-1 border border-indigo-500/20">
                            <ExternalLink size={12} /> Download PDF
                          </button>
                          <button onClick={() => handleEmailPDF(proposalVersions[0].version)} disabled={isEmailingPDF} className="text-xs px-3 py-1 bg-green-600/10 hover:bg-green-600/20 rounded-lg text-green-400 flex items-center gap-1 border border-green-500/20">
                            <Mail size={12} /> {isEmailingPDF ? 'Sending...' : 'Email PDF'}
                          </button>
                        </>
                      )}
                      <button onClick={() => handleRegenerate('proposal_pdf')} disabled={isGeneratingPDF} className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5 px-3 py-1 bg-white/[0.05] hover:bg-white/[0.08] rounded-lg border border-white/[0.06]">
                        <RefreshCw size={12} className={cn(isGeneratingPDF && 'animate-spin')} /> {isGeneratingPDF ? 'Generating PDF...' : 'Regenerate'}
                      </button>
                    </div>
                  </div>

                  {isGeneratingPDF ? (
                    <div className="flex-1 min-h-[350px] border border-white/[0.08] rounded-2xl overflow-hidden bg-slate-900/50 flex flex-col items-center justify-center">
                      <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-sm text-slate-400">Generating premium PDF proposal...</p>
                    </div>
                  ) : proposalVersions.length > 0 ? (
                    <div className="flex-1 min-h-[350px] border border-white/[0.08] rounded-2xl overflow-hidden bg-white relative flex flex-col">
                      <iframe title="B2B Growth Proposal PDF" src={`${API_BASE_URL}${proposalVersions[0].pdf_path}`} className="w-full flex-1 border-0" />
                    </div>
                  ) : (
                    <div className="flex-1 min-h-[350px] border border-white/[0.08] rounded-2xl overflow-hidden bg-slate-900/50 flex flex-col items-center justify-center">
                      <p className="text-sm text-slate-400 mb-4">No PDF proposal generated yet.</p>
                      <button onClick={() => handleRegenerate('proposal_pdf')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-semibold">Generate First PDF Proposal</button>
                    </div>
                  )}

                  {proposalVersions.length > 0 && (
                    <div className="mt-4 bg-slate-900/50 p-4 rounded-xl border border-white/[0.06]">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Version History</h4>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {proposalVersions.map((v) => (
                          <div key={v.version} className="flex justify-between items-center bg-slate-950/20 p-2 rounded-lg text-xs text-slate-300">
                            <span>Version {v.version} - {new Date(v.generated_date).toLocaleString()}</span>
                            <button onClick={() => handleDownloadPDF(v.version)} className="text-indigo-400 hover:text-indigo-300">Download</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'chat' && (
                <div className="flex-1 flex flex-col min-h-[350px] bg-slate-900/50 rounded-2xl border border-white/[0.06] overflow-hidden">
                  <div className="flex-1 p-4 overflow-y-auto space-y-4">
                    {salesAgent.conversation_history.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-20 text-slate-500 space-y-2">
                        <Sparkles size={28} className="text-slate-700 animate-pulse" />
                        <p className="text-xs">Ask anything about this lead (e.g. "Why is this lead important?")</p>
                        <div className="flex flex-wrap gap-2 justify-center max-w-md pt-3">
                          {["Why is this lead important?", "Write a quick email value pitch", "Suggest local services we should pitch"].map((query, idx) => (
                            <button key={idx} onClick={() => setChatMessage(query)} className="bg-white/[0.04] border border-white/[0.06] text-[10px] text-slate-400 px-2.5 py-1 rounded-lg hover:text-white">{query}</button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      salesAgent.conversation_history.map((msg, i) => (
                        <div key={i} className={cn("flex flex-col max-w-[80%] rounded-2xl p-4 text-xs leading-relaxed",
                          msg.role === 'user' ? "bg-indigo-600 text-white ml-auto rounded-tr-none" : "bg-slate-950/40 text-slate-300 border border-white/[0.06] rounded-tl-none")}>
                          <span className="text-[8px] font-bold text-white/50 block mb-1 uppercase tracking-widest font-mono">{msg.role === 'user' ? 'Sales Manager' : 'AI Lead Advisor'}</span>
                          <div className="whitespace-pre-wrap">{msg.content}</div>
                        </div>
                      ))
                    )}
                    <div ref={chatBottomRef} />
                  </div>

                  <form onSubmit={handleSendMessage} className="p-3 border-t border-white/[0.06] bg-slate-950/20 flex gap-2">
                    <input type="text" value={chatMessage} onChange={(e) => setChatMessage(e.target.value)} disabled={isChatSending}
                      placeholder="Ask the Sales Advisor details, angles, or custom outreach..."
                      className="flex-1 bg-white/[0.04] border border-white/[0.06] focus:border-indigo-500 outline-none rounded-xl text-xs px-3.5 text-white disabled:opacity-50" />
                    <button type="submit" disabled={!chatMessage.trim() || isChatSending} className="p-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl transition-all">
                      {isChatSending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                  </form>
                </div>
              )}

            </div>
          </div>
        )}
      </div>
    </div>
  );
}
